/**
 * Supabase-authService — drop-in tegenhanger van `src/accounts/logic/authService.ts`.
 *
 * Spiegelt opzettelijk dezelfde functienamen en resultaatvorm, zodat
 * `LoginScreen` en `RegisterScreen` kunnen omschakelen door één import te
 * wijzigen in plaats van hun logica te herschrijven.
 *
 * ─── HOE LEERLINGEN INLOGGEN ZONDER E-MAIL ──────────────────────────────────
 *
 * Supabase Auth is e-mailgebaseerd, maar leerlingen in het basisonderwijs horen
 * geen mailbox te hebben. Een gebruikersnaam wordt daarom deterministisch
 * omgezet naar een synthetisch adres:
 *
 *     sanne12  →  sanne12@learners.meesterdjay.invalid
 *
 * `.invalid` is per RFC 2606 gereserveerd en niet routeerbaar: er kan nooit per
 * ongeluk post naar een kind gaan.
 *
 * Cruciaal: de client BEREKENT dit adres zelf. Er is dus geen
 * `username → e-mail`-endpoint nodig, en dat is precies de bedoeling — zo'n
 * endpoint zou anoniem aanroepbaar moeten zijn en daarmee een
 * enumeratie-endpoint zijn waarmee je kunt aftasten welke leerlingnamen bestaan.
 */

import { supabase } from './client'
import {
  signIn as rawSignIn,
  signUp as rawSignUp,
  signOut as rawSignOut,
  type AuthResult,
  type SignUpRole,
} from './auth'
import { error as logError } from '../../utils/logger'

/** Domein voor synthetische leerling-adressen. Nooit routeerbaar. */
const LEARNER_EMAIL_DOMAIN = 'learners.meesterdjay.invalid'

/**
 * Namespace waarin een zelf-geregistreerd kind terechtkomt.
 *
 * ⚠️ BEKEND VERSCHIL MET 0005. De database maakt gebruikersnamen uniek per
 * namespace, met het oog op een tweede school die ook een `sanne` heeft. Het
 * auth-adres hierboven bevat die namespace NIET — het is
 * `sanne@learners.…invalid`, niet `sanne@solo.learners.…invalid`.
 *
 * Zolang alle accounts in 'solo' zitten kan dat geen kwaad. Zodra er een
 * schoolnamespace bijkomt, botsen twee gelijknamige leerlingen alsnog op
 * auth-niveau, want dáár is het adres wél globaal uniek. Dat moet vóór de
 * eerste school opgelost worden — en het is een wijziging van de inlogidentiteit
 * van bestaande accounts, dus niet iets om ongemerkt door te voeren.
 */
const LEARNER_DEFAULT_NAMESPACE = 'solo'

/**
 * Normaliseer een gebruikersnaam naar de vorm die de database accepteert
 * (`^[a-z0-9._-]{3,32}$`, zie migratie 0002).
 *
 * Dezelfde normalisatie moet aan beide kanten gelden: zonder lowercasing
 * zouden `Sanne12` en `sanne12` twee verschillende auth-accounts worden terwijl
 * een kind ze als hetzelfde ervaart.
 */
export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase()
}

export function isValidUsername(username: string): boolean {
  return /^[a-z0-9._-]{3,32}$/.test(normalizeUsername(username))
}

/** Gebruikersnaam → synthetisch auth-e-mailadres. Puur, geen netwerk. */
export function usernameToAuthEmail(username: string): string {
  return `${normalizeUsername(username)}@${LEARNER_EMAIL_DOMAIN}`
}

/** Is dit een synthetisch leerling-adres (en dus geen echt e-mailadres)? */
export function isSyntheticLearnerEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${LEARNER_EMAIL_DOMAIN}`)
}

// ── Sessieresultaat ─────────────────────────────────────────────────────────

export type AccountRole = 'learner' | 'parent' | 'teacher' | 'admin'

export interface AuthSession {
  accountId: string
  role: AccountRole
  displayName: string
  grade: number | null
}

/**
 * Zelfde vorm als de bestaande authService: een mislukking is een WAARDE, geen
 * exception. Zo kan een faalpad niet stil verdwijnen in een `catch {}`.
 */
export interface LoginResult {
  ok: boolean
  session?: AuthSession
  message?: string
}

/** Haal het profiel op dat bij de net-ingelogde sessie hoort. */
async function loadSession(): Promise<LoginResult> {
  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user) {
    return { ok: false, message: 'Sessie kon niet worden gelezen.' }
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, display_name, grade')
    .eq('id', userData.user.id)
    .single()

  if (error || !data) {
    // Inloggen lukte, maar er is geen profielrij. Dat duidt op een mislukte
    // signup-trigger — een inconsistente staat die we niet als succes mogen
    // presenteren, want de app zou dan zonder rol verder gaan.
    logError('[SupabaseAuth] Sessie zonder profielrij', error)
    return {
      ok: false,
      message: 'Je account is niet volledig aangemaakt. Neem contact op met de beheerder.',
    }
  }

  return {
    ok: true,
    session: {
      accountId: data.id as string,
      role: data.role as AccountRole,
      displayName: data.display_name as string,
      grade: (data.grade as number | null) ?? null,
    },
  }
}

// ── Inloggen ────────────────────────────────────────────────────────────────

/** Leerling-login: gebruikersnaam + wachtwoord. */
export async function loginWithUsername(
  username: string,
  password: string,
): Promise<LoginResult> {
  if (!isValidUsername(username)) {
    // Neutrale melding: verklap niet of deze gebruikersnaam bestaat.
    return { ok: false, message: 'Gebruikersnaam of wachtwoord is niet juist.' }
  }
  const result = await rawSignIn(usernameToAuthEmail(username), password)
  if (!result.ok) {
    return {
      ok: false,
      message: 'Gebruikersnaam of wachtwoord is niet juist.',
    }
  }
  return loadSession()
}

/** Ouder/leerkracht/beheerder-login: e-mail + wachtwoord. */
export async function loginWithEmail(
  email: string,
  password: string,
): Promise<LoginResult> {
  // Blokkeer het synthetische domein op de e-mailroute: anders kan iemand een
  // leerlingaccount aanvallen via het formulier dat voor volwassenen bedoeld is.
  if (isSyntheticLearnerEmail(email)) {
    return { ok: false, message: 'E-mailadres of wachtwoord is niet juist.' }
  }
  const result = await rawSignIn(email, password)
  if (!result.ok) return { ok: false, message: result.message }
  return loadSession()
}

/**
 * ⛔ OPZETTELIJK NIET GEÏMPLEMENTEERD.
 *
 * De localStorage-versie had `loginWithLinkCode()`: inloggen met alleen een
 * korte code, zonder wachtwoord. Dat kan niet mee naar productie.
 *
 * Een korte code is een raadbaar geheim. Geeft die code volledige toegang tot
 * het account van een kind — voortgang, toetsresultaten, naam, groep — dan is
 * brute-force op een handvol tekens genoeg om bij de gegevens van een
 * willekeurig kind te komen. Voor kinderdata is dat niet verdedigbaar.
 *
 * Een koppelcode bewijst vanaf nu een RELATIE, niet een IDENTITEIT: een reeds
 * ingelogde ouder koppelt zichzelf aan een kind via
 * `claimLearnerByLinkCode()`. Inloggen gaat altijd met gebruikersnaam of
 * e-mail + wachtwoord.
 */
export async function loginWithLinkCode(): Promise<LoginResult> {
  return {
    ok: false,
    message:
      'Inloggen met een koppelcode is niet meer mogelijk. Log in met je gebruikersnaam en wachtwoord.',
  }
}

// ── Registreren ─────────────────────────────────────────────────────────────

export interface RegisterInput {
  role: SignUpRole
  displayName: string
  password: string
  /** Verplicht voor learners. */
  username?: string
  /** Verplicht voor parents/teachers. */
  email?: string
  grade?: number
}

export async function register(input: RegisterInput): Promise<LoginResult> {
  let email: string

  if (input.role === 'learner') {
    if (!input.username || !isValidUsername(input.username)) {
      return {
        ok: false,
        message:
          'Kies een gebruikersnaam van 3-32 tekens: kleine letters, cijfers, punt, streepje of underscore.',
      }
    }
    email = usernameToAuthEmail(input.username)
  } else {
    if (!input.email || !input.email.includes('@')) {
      return { ok: false, message: 'Vul een geldig e-mailadres in.' }
    }
    if (isSyntheticLearnerEmail(input.email)) {
      return { ok: false, message: 'Dit e-mailadres is niet toegestaan.' }
    }
    email = input.email
  }

  if (input.password.length < 8) {
    return { ok: false, message: 'Kies een wachtwoord van minimaal 8 tekens.' }
  }

  // ── Leerlingen: via de Edge Function, niet via signUp ─────────────────
  //
  // Sinds e-mailbevestiging aan staat, is `supabase.auth.signUp()` voor een
  // leerling een doodlopende weg: zijn adres eindigt op `.invalid` en kan geen
  // post ontvangen, dus de bevestigingsmail komt nooit aan. Het account blijft
  // dan onbevestigd achter en het kind ziet "check je mailbox" — een mailbox
  // die niet bestaat. Ondertussen verbrandt elke poging mailquota.
  //
  // `create-learner` maakt het account serverside aan met `email_confirm: true`.
  // Er valt dan niets te bevestigen en er gaat geen mail uit. Daarna loggen we
  // gewoon normaal in; de functie deelt bewust geen sessie uit.
  if (input.role === 'learner') {
    const { data: fnData, error: fnError } = await supabase.functions.invoke(
      'create-learner',
      {
        body: {
          email,
          password: input.password,
          displayName: input.displayName,
          username: input.username,
          usernameNamespace: LEARNER_DEFAULT_NAMESPACE,
          grade: input.grade ?? null,
        },
      },
    )

    if (fnError) {
      // De functie geeft een leesbare Nederlandse melding terug in de body.
      // Die willen we tonen in plaats van "Edge Function returned a non-2xx
      // status code", want dat zegt een kind niets.
      const message = await readFunctionError(fnError)
      return { ok: false, message }
    }
    if (!fnData?.id) {
      return { ok: false, message: 'Aanmaken is niet gelukt. Probeer het opnieuw.' }
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: input.password,
    })
    if (signInError) {
      // Het account bestaat wél. Opnieuw registreren zou nu "gebruikersnaam
      // bezet" geven, dus sturen we naar inloggen in plaats van naar een lus.
      return {
        ok: false,
        message: 'Je account is aangemaakt. Log in met je gebruikersnaam.',
      }
    }
    return loadSession()
  }

  // ── Volwassenen: gewone signUp, mét bevestiging ───────────────────────
  const result = await rawSignUp({
    email,
    password: input.password,
    displayName: input.displayName,
    role: input.role,
    grade: input.grade,
  })
  if (!result.ok) return { ok: false, message: result.message }

  // Bij e-mailconfirmatie is er nog geen sessie. Dat is geen fout.
  const { data } = await supabase.auth.getSession()
  if (!data.session) {
    return {
      ok: true,
      message: 'Check je mailbox om je account te bevestigen.',
    }
  }
  return loadSession()
}

/**
 * Haalt de Nederlandse melding uit een mislukte Edge Function-aanroep.
 *
 * `functions.invoke` gooit bij een 4xx/5xx een `FunctionsHttpError` waarvan de
 * `message` alleen de statuscode noemt. De bruikbare tekst zit in de body, en
 * die moet apart uitgelezen worden.
 */
async function readFunctionError(error: unknown): Promise<string> {
  const fallback = 'Aanmaken is niet gelukt. Probeer het opnieuw.'
  const context = (error as { context?: Response })?.context
  if (!context || typeof context.json !== 'function') return fallback
  try {
    const body = await context.json()
    return typeof body?.error === 'string' ? body.error : fallback
  } catch {
    return fallback
  }
}

// ── Overig ──────────────────────────────────────────────────────────────────

export async function logout(): Promise<AuthResult> {
  return rawSignOut()
}

/**
 * Koppel een ingelogde OUDER aan een kind via de koppelcode van dat kind.
 * Retourneert het learner-id bij succes.
 */
export async function claimLearnerByLinkCode(
  code: string,
): Promise<{ ok: boolean; learnerId?: string; message?: string }> {
  const { data, error } = await supabase.rpc('claim_learner_by_link_code', {
    code,
  })
  if (error) {
    return { ok: false, message: 'Koppelcode is niet geldig.' }
  }
  return { ok: true, learnerId: data as string }
}

/** Huidige sessie herstellen bij app-start (bijv. na een refresh). */
export async function restoreSession(): Promise<LoginResult> {
  const { data } = await supabase.auth.getSession()
  if (!data.session) return { ok: false }
  return loadSession()
}
